import { useState } from "react";
import AviationSectorPicker from "../../../components/Sectors/AviationSectorPicker";
import FormSubmitButton from "../../../components/FormSubmitButton";
import LearningPathCard from "../../../components/BuildSkills/LearningPathCard";
import SubFlowHeader from "../../../components/GoalSubflow/SubFlowHeader";
import OptionPills from "../../../components/GoalSubflow/OptionPills";
import { useGoalSubflow } from "./useGoalSubflow";
import { getOptions } from "../../registry";
import { useFlowState } from "../../../state/FlowProvider";
import { t } from "../../../../../locales/t";

/**
 * "Skills roadmap": the build-skills sub-flow. It asks for sectors only when it
 * owns the seeking sector question; otherwise it names the sectors collected by
 * `asks.recommendFrom` and asks only how the member likes to learn.
 */
const GOAL = "build_skills";

const BuildSkills = () => {
  const { asks, index, total, answers, submit } = useGoalSubflow(GOAL);
  const state = useFlowState();

  const [sectors, setSectors] = useState(
    () => answers.sectors ?? { primary: [], secondary: {} },
  );
  const [learningFormats, setLearningFormats] = useState(
    () => answers.learningFormats ?? [],
  );

  // Resolved here rather than in the card: ids belong to the flow, labels to
  // the view, and the card should not need to know either vocabulary.
  const recommendedFrom = (
    state.goalData?.[asks.recommendFrom]?.sectors?.primary ?? []
  ).map((id) => t(`onboarding.vocab.${id}`));

  const canContinue =
    (!asks.sectors || sectors.primary.length > 0) &&
    (!asks.learningFormats || learningFormats.length > 0);

  const handleContinue = () => {
    if (!canContinue) return;
    submit({
      ...(asks.sectors ? { sectors } : {}),
      ...(asks.learningFormats ? { learningFormats } : {}),
    });
  };

  return (
    <SubFlowHeader
      goal={GOAL}
      index={index}
      total={total}
      onSubmit={handleContinue}
    >
      {asks.recommendFrom && <LearningPathCard sectors={recommendedFrom} />}

      {asks.sectors && (
        <AviationSectorPicker
          value={sectors}
          onChange={setSectors}
          mode={asks.sectors}
          label={t(`onboarding.subflow.${GOAL}.sectorsLabel`)}
          subtitle={t(`onboarding.subflow.${GOAL}.sectorsSubtitle`)}
        />
      )}

      {asks.learningFormats && (
        <OptionPills
          multiple
          options={getOptions("learningFormats")}
          value={learningFormats}
          onChange={setLearningFormats}
          label={t("onboarding.subflow.questions.learningFormats.label")}
        />
      )}

      <FormSubmitButton
        variant="contained"
        fullWidth
        disabledReason={!canContinue ? t("onboarding.subflow.incomplete") : ""}
        sx={{ py: 1.5 }}
      >
        {t("onboarding.subflow.continue")}
      </FormSubmitButton>
    </SubFlowHeader>
  );
};

export default BuildSkills;
